<script setup lang="ts">
import type { IconComponent } from '@/modules/common/types/icon.type.ts';
import { computed } from 'vue';

const {
  tag = 'div',
  inline,
  checked,
  disabled,
} = defineProps<{
  label: string;
  icon: IconComponent;
  tag?: 'div' | 'li';
  inline?: boolean;
  checked?: boolean;
  disabled?: boolean;
}>();

const emit = defineEmits<{
  click: [];
}>();

const className = computed(() => ({
  _inline: inline,
  _checked: checked,
  _disabled: disabled,
}));

function onClick() {
  if (!disabled) {
    emit('click');
  }
}
</script>

<template>
  <component
    :is="tag"
    class="ui-tile"
    :class="className"
    @click="onClick"
  >
    <div
      class="label"
      v-html="$t(label)"
    />
    <component
      :is="icon"
      class="icon"
    />
  </component>
</template>

<style scoped lang="scss">
.ui-tile {
  padding: 8px;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;

  color: var(--color-gray-700);
  background-color: var(--color-gray-075);
  border-radius: 8px;

  transition:
    background-color var(--transition-fast),
    box-shadow var(--transition-fast);

  &._checked {
    background-color: var(--color-gray-100);
  }

  &._disabled {
    opacity: 0.5;
  }

  &._inline {
    flex-direction: row-reverse;

    .icon {
      margin-right: 8px;
    }
  }

  &:not(._disabled) {
    cursor: pointer;

    @media (hover: hover) {
      &:hover {
        box-shadow: var(--shadow-control);
      }
    }
  }
}

.label {
  margin-bottom: 4px;
  text-align: center;
  font-size: var(--font-size-l);
  line-height: var(--line-height-l);
}
</style>
